/**
 * Wire protocol for the character-runtime live mutation path.
 *
 * TypeScript mirror of src/lambda/shared/GoobDoobVtt.Domain/RuntimeMessages.cs.
 * Both implementations must stay in lockstep — when one changes, the other has
 * to change too.
 *
 * Action constants flow client→server in the WebSocket $default dispatcher.
 * Server type constants flow server→client inside ServerEnvelope. The two
 * deliberately use different namespaces because client and server speak
 * different verbs: the client says "do this", the server says "this happened".
 */

import type { ActiveCondition } from './runtime';

export const RuntimeActions = {
  HpUpdate:          'character/hp',
  SlotsUpdate:       'character/slots',
  HitDiceUpdate:     'character/hit-dice',
  InspirationUpdate: 'character/inspiration',
  ConditionsUpdate:  'character/conditions',
} as const;

/**
 * Spell-slot map shape — mirrors C# SpellSlots in CharacterRuntime.cs.
 *
 * `leveled` keys are stringified slot levels ("1".."9") because JSON object
 * keys are always strings; the C# side uses Dictionary&lt;int,int&gt; and
 * System.Text.Json round-trips int keys as strings, so this is the on-wire
 * shape both sides see. Values are EXPENDED counts, not remaining.
 *
 * `pactExpended` is null for non-warlock characters.
 */
export interface SpellSlotsState {
  leveled: Record<string, number>;
  pactExpended: number | null;
}

export const RuntimeServerTypes = {
  RuntimePatch:    'character/runtime-patch',
  RuntimeRejected: 'character/runtime-rejected',
} as const;

/**
 * HP mutation request. Either currentHp or tempHp (or both) must be provided —
 * undefined means "leave this field alone". expectedVersion is the version the
 * client believed it was editing; on a mismatch the server returns a
 * RuntimeRejectedPayload and the client reconciles by adopting the new
 * version stamp and re-issuing the intended delta.
 */
export interface HpUpdateRequest {
  action: typeof RuntimeActions.HpUpdate;
  characterId: string;
  expectedVersion: number;
  currentHp?: number;
  tempHp?: number;
  reason?: string;
}

/**
 * Replaces the entire spell-slot map. The client computes the new map locally
 * (apply the intended delta on top of the snapshot it has) and ships the full
 * thing — the server overwrites under a version check. See SlotsUpdateRequest
 * in RuntimeMessages.cs for the rationale on full-replace vs deltas.
 */
export interface SlotsUpdateRequest {
  action: typeof RuntimeActions.SlotsUpdate;
  characterId: string;
  expectedVersion: number;
  spellSlots: Record<string, SpellSlotsState>;
  reason?: string;
}

/**
 * Hit-dice spent counter. Sheet A and Sheet B always level together so the
 * merged play view only needs one integer. Max hit dice = combined level —
 * enforced client-side, the server trusts the value.
 */
export interface HitDiceUpdateRequest {
  action: typeof RuntimeActions.HitDiceUpdate;
  characterId: string;
  expectedVersion: number;
  hitDiceSpent: number;
  reason?: string;
}

/**
 * Binary inspiration flag. Carries the absolute new value (not a toggle)
 * so two concurrent grants can't double-flip back to false.
 */
export interface InspirationUpdateRequest {
  action: typeof RuntimeActions.InspirationUpdate;
  characterId: string;
  expectedVersion: number;
  inspiration: boolean;
  reason?: string;
}

/**
 * Replaces the entire active-conditions list. Same full-replace rationale as
 * spell slots: small bounded structure, and shipping the whole list sidesteps
 * per-element contention on a server that only version-checks the row. The
 * client computes the new list locally (add/remove/exhaustion-level bump) and
 * the server overwrites the attribute under the version check.
 */
export interface ConditionsUpdateRequest {
  action: typeof RuntimeActions.ConditionsUpdate;
  characterId: string;
  expectedVersion: number;
  conditions: ActiveCondition[];
  reason?: string;
}

export interface RuntimePatchPayload {
  characterId: string;
  version: number;
  updatedAt: string;
  currentHp?: number;
  tempHp?: number;
  /** Present only when the mutation touched the slot map. */
  spellSlots?: Record<string, SpellSlotsState>;
  /** Present only on hit-dice mutations. */
  hitDiceSpent?: number;
  /** Present only on inspiration mutations. */
  inspiration?: boolean;
  /** Present only when the mutation touched the conditions list; full new list. */
  conditions?: ActiveCondition[];
}

export interface RuntimeRejectedPayload {
  characterId: string;
  reason: string;
  version: number;
  currentHp?: number;
  tempHp?: number;
  /** Server-current slot map on slot-mutation rejections. */
  spellSlots?: Record<string, SpellSlotsState>;
  /** Server-current spent counter on hit-dice rejections. */
  hitDiceSpent?: number;
  /** Server-current inspiration flag on inspiration rejections. */
  inspiration?: boolean;
  /** Server-current conditions list on conditions rejections. */
  conditions?: ActiveCondition[];
}

/**
 * Generic server envelope shape. Matches ServerEnvelope&lt;T&gt; on the C# side.
 */
export interface ServerEnvelope<T> {
  type: string;
  payload: T;
}
